// Auth Utilities - Modular helper functions
import { supabase } from '@config/supabase';
import type { Session } from '@supabase/supabase-js';
import { UserPlan } from '@types';
import type { AuthUser } from './types';

const PLAN_MAP: Record<string, UserPlan> = {
  FREE: UserPlan.FREE,
  BEDROOM: UserPlan.BEDROOM,
  PRO: UserPlan.PRO,
  STUDIO: UserPlan.STUDIO,
  free: UserPlan.FREE,
  bedroom: UserPlan.BEDROOM,
  pro: UserPlan.PRO,
  studio: UserPlan.STUDIO,
};

const SUPABASE_AUTH_STORAGE_KEY = 'sb-auth-token';

/** Liest den ersten gefundenen Supabase-Auth-Token aus localStorage (sb-auth-token oder sb-*-auth-token). */
function getSupabaseTokenRaw(): string | null {
  if (typeof window === 'undefined') return null;
  const fromKey = (key: string): string | null => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const data = JSON.parse(raw) as { access_token?: string; current_session?: { access_token?: string }; session?: { access_token?: string } };
      return data?.access_token ?? data?.current_session?.access_token ?? data?.session?.access_token ?? null;
    } catch {
      return null;
    }
  };
  let t = fromKey(SUPABASE_AUTH_STORAGE_KEY);
  if (t) return t;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
      t = fromKey(key);
      if (t) return t;
    }
  }
  return null;
}

/**
 * Minimalen User synchron aus dem Supabase-JWT (localStorage) bauen – für ersten Paint ohne sessionStorage.
 */
export function getInitialUserFromStorage(): AuthUser | null {
  const token = getSupabaseTokenRaw();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as {
      sub?: string;
      email?: string;
      app_metadata?: { user_plan?: string };
    };
    const id = payload?.sub;
    if (!id) return null;
    const plan = getInitialPlanFromStorage();
    return {
      id,
      email: payload?.email ?? '',
      username: payload?.email?.split('@')[0] ?? 'User',
      plan,
      storageUsed: 0,
      storageLimit: 1073741824,
      storageAddons: 0,
      features: [],
      isSuspended: false,
      billingInterval: 'monthly',
      subscriptionStatus: null,
      subscriptionRenewsAt: null,
      planExpiresAt: null,
    };
  } catch {
    return null;
  }
}

/**
 * Plan synchron aus localStorage (Supabase JWT) lesen – vor dem ersten Render.
 * Probiert sb-auth-token und alle sb-*-auth-token Keys.
 */
export function getInitialPlanFromStorage(): UserPlan {
  if (typeof window === 'undefined') return UserPlan.FREE;
  const token = getSupabaseTokenRaw();
  if (!token) return UserPlan.FREE;
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { app_metadata?: { user_plan?: string } };
    const rawPlan = payload?.app_metadata?.user_plan;
    if (!rawPlan) return UserPlan.FREE;
    const normalized = String(rawPlan).toUpperCase().trim();
    return PLAN_MAP[normalized] ?? PLAN_MAP[rawPlan] ?? UserPlan.FREE;
  } catch {
    return UserPlan.FREE;
  }
}

/** Plan aus JWT (Custom Access Token Hook: app_metadata.user_plan) – sofort verfügbar. Fallback: Zugriff aus access_token-Payload. */
export function getPlanFromSession(session: Session): UserPlan {
  const u = session?.user;
  if (!u) return UserPlan.FREE;
  let raw = (u.app_metadata as { user_plan?: string } | undefined)?.user_plan;
  if (raw) {
    const normalized = String(raw).toUpperCase().trim();
    return PLAN_MAP[normalized] ?? PLAN_MAP[raw] ?? UserPlan.FREE;
  }
  try {
    const token = (session as { access_token?: string }).access_token;
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1])) as { app_metadata?: { user_plan?: string } };
      raw = payload?.app_metadata?.user_plan;
      if (raw) {
        const normalized = String(raw).toUpperCase().trim();
        return PLAN_MAP[normalized] ?? PLAN_MAP[raw] ?? UserPlan.FREE;
      }
    }
  } catch (_) {}
  return UserPlan.FREE;
}

/**
 * Create basic user from Supabase session.
 * Username nur aus E-Mail-Prefix – kein Google-/OAuth-Name, damit kein Flackern wenn danach Profil (full_name) aus DB geladen wird.
 */
export function createUserFromSession(session: Session): AuthUser {
  const authUser = session.user;
  
  return {
    id: authUser.id,
    email: authUser.email || '',
    username: authUser.email?.split('@')[0] || 'User',
    plan: getPlanFromSession(session),
    storageUsed: 0,
    storageLimit: 1073741824,
    storageAddons: 0,
    features: [],
    isSuspended: false,
    billingInterval: 'monthly',
    subscriptionStatus: null,
    subscriptionRenewsAt: null,
    planExpiresAt: null,
  };
}

/**
 * Fetch full user profile from database
 */
export async function fetchUserProfileFromDB(userId: string): Promise<AuthUser | null> {
  try {
    // Only select columns that actually exist in the profiles table
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, plan, billing_interval, storage_limit, storage_used, storage_addons_total_bytes, is_suspended, suspension_download_enabled, suspension_download_expires_at, suspension_started_at, suspension_grace_period_days, stripe_subscription_status, stripe_subscription_current_period_end, plan_expires_at, deleted_at, mfa_enabled')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('❌ Profile fetch error:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code,
      });
      
      // If profile doesn't exist (404 or PGRST116), the account might be deleted
      if (error.code === 'PGRST116' || error.message?.includes('No rows') || error.message?.includes('not found')) {
        // Check if auth user still exists (might be hard-deleted)
        try {
          const { data: { user: authUser }, error: authError } = await supabase.auth.getUser(userId);
          if (authError || !authUser) {
            // Auth user also doesn't exist - account was fully deleted
            throw new Error('ACCOUNT_DELETED');
          }
          // Auth user exists but profile doesn't - account was hard-deleted
          throw new Error('ACCOUNT_DELETED');
        } catch (checkError: any) {
          if (checkError.message === 'ACCOUNT_DELETED') {
            throw checkError;
          }
        }
      }
      
      return null;
    }

    if (!profile) {
      // Profile doesn't exist - check if auth user exists
      try {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser(userId);
        if (authError || !authUser) {
          // Auth user also doesn't exist - account was fully deleted
          throw new Error('ACCOUNT_DELETED');
        }
        // Auth user exists but profile doesn't - account was hard-deleted
        throw new Error('ACCOUNT_DELETED');
      } catch (checkError: any) {
        if (checkError.message === 'ACCOUNT_DELETED') {
          throw checkError;
        }
      }
      return null;
    }

    // Check if account is soft-deleted - throw error if deleted
    if (profile.deleted_at) {
      throw new Error('ACCOUNT_DELETED');
    }

    // Check if account is suspended - but don't sign out here, let the caller handle it
    const isSuspended = profile.is_suspended === true;

    // Map database plan to UserPlan enum
    const planMap: Record<string, UserPlan> = {
      'FREE': UserPlan.FREE,
      'BEDROOM': UserPlan.BEDROOM,
      'PRO': UserPlan.PRO,
      'STUDIO': UserPlan.STUDIO,
      'free': UserPlan.FREE,
      'bedroom': UserPlan.BEDROOM,
      'pro': UserPlan.PRO,
      'studio': UserPlan.STUDIO,
    };

    const billingInterval = profile.billing_interval === 'yearly' ? 'yearly' : 'monthly';

    // Use email from database - it's automatically synced from auth.users via trigger
    // Realtime will push updates automatically
    const email = profile.email || '';

    // Normalize plan value - handle null, undefined, and different cases
    const rawPlan = profile.plan;
    const normalizedPlan = rawPlan ? String(rawPlan).toLowerCase().trim() : 'free';
    const mappedPlan = planMap[normalizedPlan] || planMap[rawPlan as string] || UserPlan.FREE;

    const userData: AuthUser = {
      id: userId,
      email, // Email from database (synced via trigger, updated via Realtime)
      username: profile.full_name || email.split('@')[0] || 'User',
      plan: mappedPlan,
      storageUsed: profile.storage_used || 0,
      storageLimit: profile.storage_limit || 1073741824,
      storageAddons: profile.storage_addons_total_bytes || 0,
      features: [], // features column doesn't exist in profiles table
      isSuspended: isSuspended,
      mfaEnabled: profile.mfa_enabled === true,
      billingInterval,
      subscriptionStatus: profile.stripe_subscription_status ?? null,
      subscriptionRenewsAt: profile.stripe_subscription_current_period_end ?? null,
      planExpiresAt: profile.plan_expires_at ?? null,
    };

    return userData;
  } catch (error) {
    console.error('❌ Error fetching user profile:', error);
    return null;
  }
}

/**
 * Build email redirect URL for verification
 */
export function buildVerificationRedirectUrl(): string {
  return `${window.location.origin}/verify-email`;
}

